import { useEffect, useMemo, useRef, useState, useContext } from 'react';
import { createPortal } from 'react-dom';
import { estiloChip, type ItemChip } from '@/shared/components/ui/ChipSelect';
import { useI18n } from '@/shared/i18n/useT';
import { flexClass, contentStyle } from './alignment';
import { useDropdownKeyboard } from './dropdownKeyboard';
import { DEFAULT_ALIGNMENT, type CellAlign, type CellRule } from './types';
import { GridConfigContext } from './GridConfigProvider';
import { Icon } from '@/shared/components/ui/Icon';
import { ResizeCorner, useResizableSize } from './ResizeCorner';

/** Below this much room under the cell, the list opens upward instead. */
const ALTURA_MAXIMA = 224;
/** Gap kept between the list and the window's edge. */
const MARGEN_BORDE = 8;

export interface ListChipEditorProps {
  items: ItemChip[];
  initialText?: string;
  onConfirm: (valor: string) => void;
  onCancel: () => void;
  placeholder?: string;
  /** Shown only to those who may edit the column's options: opens its options sidebar. */
  onEditOptions?: () => void;
}

/** Size options shared by the chip and autofill lists, so a resize carries over between them. */
export const LIST_SIZE_OPTIONS = { key: 'numtable-cell-list-size', minWidth: 160, minHeight: 100 };

/**
 * Footer under a chip / autofill list: the pencil that opens the column's options sidebar
 * (only for those who may edit them) and the corner that resizes the list.
 */
export function EditOptionsFooter({
  onEditOptions,
  onResize,
  onResetSize,
}: {
  onEditOptions?: (() => void) | undefined;
  onResize: (event: React.MouseEvent) => void;
  /** Double click on the corner: fit the list to its options again. */
  onResetSize: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="relative flex min-h-[30px] shrink-0 items-center justify-end gap-1 border-t border-border bg-surface-2 py-0.5 pl-2 pr-4">
      {onEditOptions && <button
        type="button"
        title={t('grid.editOptions')}
        aria-label={t('grid.editOptions')}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation();
          onEditOptions();
        }}
        className="flex h-7 w-7 items-center justify-center rounded text-text hover:bg-surface-3"
      >
        <Icon name="pencil" className="h-3.5 w-3.5" />
      </button>}
      {/* Pinned to the panel's very corner, just inside its rounding. */}
      <span className="absolute bottom-0 right-0 flex h-4 w-4">
        <ResizeCorner onMouseDown={onResize} onDoubleClick={onResetSize} />
      </span>
    </div>
  );
}

/**
 * Inline editor (combobox) for chip cells: an input that opens the list upon typing
 * and filters matching options. Used via Grid's `cellEditor` prop (shown on double click, F2,
 * or typing over the cell). Fixed portal dropdown prevents clipping by grid scroll containers.
 */
export function ListChipEditor({
  items,
  initialText,
  onConfirm,
  onCancel,
  placeholder,
  onEditOptions,
}: ListChipEditorProps) {
  const { t } = useI18n();
  const defaultPlaceholder = placeholder ?? t('grid.smartchipEmptyValue');

  const [filtro, setFiltro] = useState(initialText ?? '');
  const [posicion, setPosicion] = useState({ top: 0, left: 0, width: 0, haciaArriba: false, altoMaximo: ALTURA_MAXIMA });
  const inputRef = useRef<HTMLInputElement>(null);
  const contenedorRef = useRef<HTMLDivElement>(null);
  const cerradoRef = useRef(false);
  const { panelRef, size, startResize, resetSize } = useResizableSize(null, LIST_SIZE_OPTIONS);

  const filtrados = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => i.texto.toLowerCase().includes(q));
  }, [filtro, items]);

  const opciones = useMemo(() => ['', ...filtrados.map((i) => i.clave)], [filtrados]);

  useEffect(() => {
    inputRef.current?.focus();
    if (!initialText) inputRef.current?.select();
  }, [initialText]);

  useEffect(() => {
    const actualizar = () => {
      const r = contenedorRef.current?.getBoundingClientRect();
      if (!r) return;
      // The visible height, which leaves out a phone keyboard; innerHeight may not.
      const espacioAbajo = (window.visualViewport?.height ?? window.innerHeight) - r.bottom;
      const haciaArriba = espacioAbajo < ALTURA_MAXIMA && r.top > espacioAbajo;
      // Tall enough for every option, up to the edge of the window on the side it opens.
      const altoMaximo = (haciaArriba ? r.top : espacioAbajo) - MARGEN_BORDE;
      setPosicion({ top: haciaArriba ? r.top : r.bottom, left: r.left, width: r.width, haciaArriba, altoMaximo });
    };
    actualizar();
    window.addEventListener('scroll', actualizar, true);
    window.addEventListener('resize', actualizar);
    window.visualViewport?.addEventListener('resize', actualizar);
    return () => {
      window.removeEventListener('scroll', actualizar, true);
      window.removeEventListener('resize', actualizar);
      window.visualViewport?.removeEventListener('resize', actualizar);
    };
  }, [filtro]);

  const confirmar = (v: string) => {
    cerradoRef.current = true;
    onConfirm(v);
  };
  const cancelar = () => {
    cerradoRef.current = true;
    onCancel();
  };

  const { indice, setIndice, refActiva, alTeclear } = useDropdownKeyboard({
    cantidad: opciones.length,
    indiceInicial: filtrados.length > 0 ? 1 : 0,
    reiniciarCon: filtro,
    elegir: (i) => {
      if (i === 0 && filtrados.length > 0 && filtro.trim()) {
        confirmar(filtrados[0]?.clave ?? '');
      } else {
        confirmar(opciones[i] ?? (filtrados[0]?.clave ?? ''));
      }
    },
    cerrar: cancelar,
  });

  return (
    <div ref={contenedorRef} className="relative">
      <input
        ref={inputRef}
        value={filtro}
        onChange={(e) => setFiltro(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            if (filtrados.length > 0 && (indice <= 0 || !opciones[indice])) {
              e.preventDefault();
              confirmar(filtrados[0]!.clave);
              return;
            }
          }
          alTeclear(e);
        }}
        onClick={(e) => e.stopPropagation()}
        onBlur={() => {
          if (!cerradoRef.current) onCancel();
        }}
        placeholder={t('grid.smartchipFilterPlaceholder')}
        className="h-7 w-full rounded border-none bg-surface px-2 text-sm text-text outline-none"
      />
      {createPortal(
        <div
          ref={panelRef}
          role="menu"
          data-dropdown-open="1"
          style={{
            position: 'fixed',
            top: posicion.top,
            left: posicion.left,
            // As wide as the longest option until resized, never past the window's edge.
          width: size?.width ?? 'max-content',
          minWidth: size ? undefined : 140,
          maxWidth: window.innerWidth - posicion.left - MARGEN_BORDE,
          height: size?.height,
            maxHeight: size ? undefined : posicion.altoMaximo,
            transform: posicion.haciaArriba ? 'translateY(calc(-100% - 4px))' : undefined,
            marginTop: posicion.haciaArriba ? 0 : 4,
          }}
          className="z-50 flex flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-card"
        >
          <div className={`min-h-0 flex-1 overflow-y-auto p-1`}>
          <button
            type="button"
            ref={indice === 0 ? refActiva : undefined}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => confirmar('')}
            className={`block w-full rounded-lg px-2 py-1 text-left text-sm text-muted hover:bg-surface-3 ${indice === 0 ? 'bg-surface-3' : ''}`}
          >
            {defaultPlaceholder}
          </button>
          {filtrados.length === 0 && (
            <p className="px-2 py-1.5 text-sm text-muted">{t('grid.smartchipNoMatches')}</p>
          )}
          {filtrados.map((item, i) => (
            <button
              key={item.clave}
              type="button"
              ref={indice === i + 1 ? refActiva : undefined}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => confirmar(item.clave)}
              onMouseEnter={() => setIndice(i + 1)}
              className={`block w-full rounded-lg px-2 py-0.5 text-left hover:bg-surface-3 ${indice === i + 1 ? 'bg-surface-3' : ''}`}
            >
              <span
                className="inline-block max-w-full truncate rounded-full border border-transparent px-2.5 py-0.5 text-xs font-bold"
                style={estiloChip(item.color)}
              >
                {item.texto}
              </span>
            </button>
          ))}
          </div>
          <EditOptionsFooter onEditOptions={onEditOptions} onResize={startResize}
              onResetSize={resetSize} />
        </div>,
        document.body,
      )}
    </div>
  );
}

export interface ListChipCellProps {
  items: ItemChip[];
  value: string;
  editable: boolean;
  onChange: (valor: string) => void;
  placeholder?: string;
  /** `auto` defers to the shared alignment rule, like every other cell. */
  align?: CellAlign | 'auto';
  wrapMode?: 'wrap' | 'clip' | 'shrink' | 'expand' | 'overflow';
  /** Shown only to those who may edit the column's options: opens its options sidebar. */
  onEditOptions?: () => void;
}

/**
 * Google Sheets-style smartchip cell for list-driven columns (status, category, etc.):
 * pill badge filling the cell with dropdown toggle arrow.
 */
export function ListChipCell({
  items,
  value,
  editable,
  onChange,
  placeholder,
  align,
  wrapMode,
  onEditOptions,
}: ListChipCellProps) {
  const { t } = useI18n();
  const defaultPlaceholder = placeholder ?? t('grid.smartchipEmptyValue');

  const [abierto, setAbierto] = useState(false);
  const [filtro, setFiltro] = useState('');
  const [posicion, setPosicion] = useState({ top: 0, left: 0, width: 0, haciaArriba: false, altoMaximo: ALTURA_MAXIMA });
  const contenedorRef = useRef<HTMLDivElement>(null);
  const { panelRef, size, startResize, resetSize } = useResizableSize(null, LIST_SIZE_OPTIONS);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const gridConfig = useContext(GridConfigContext);
  const regla: CellRule = gridConfig?.alignment?.smartchip ?? DEFAULT_ALIGNMENT.smartchip;
  const contenido = contentStyle(regla, false, wrapMode);

  const seleccionado = useMemo(() => items.find((i) => i.clave === value), [items, value]);
  const texto = seleccionado?.texto ?? value;
  const color = seleccionado?.color ?? null;

  const filtrados = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => i.texto.toLowerCase().includes(q));
  }, [filtro, items]);

  const opciones = useMemo(() => ['', ...filtrados.map((i) => i.clave)], [filtrados]);

  useEffect(() => {
    if (!abierto) {
      setFiltro('');
      return;
    }
    const actualizar = () => {
      const r = contenedorRef.current?.getBoundingClientRect();
      if (!r) return;
      // The visible height, which leaves out a phone keyboard; innerHeight may not.
      const espacioAbajo = (window.visualViewport?.height ?? window.innerHeight) - r.bottom;
      const haciaArriba = espacioAbajo < ALTURA_MAXIMA && r.top > espacioAbajo;
      // Tall enough for every option, up to the edge of the window on the side it opens.
      const altoMaximo = (haciaArriba ? r.top : espacioAbajo) - MARGEN_BORDE;
      setPosicion({ top: haciaArriba ? r.top : r.bottom, left: r.left, width: r.width, haciaArriba, altoMaximo });
    };
    actualizar();
    const cerrarFuera = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!contenedorRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        setAbierto(false);
      }
    };
    window.addEventListener('scroll', actualizar, true);
    window.addEventListener('resize', actualizar);
    document.addEventListener('mousedown', cerrarFuera, true);
    return () => {
      window.removeEventListener('scroll', actualizar, true);
      window.removeEventListener('resize', actualizar);
      document.removeEventListener('mousedown', cerrarFuera, true);
    };
  }, [abierto]);

  const elegir = (v: string) => {
    onChange(v);
    setAbierto(false);
  };

  const { indice, setIndice, refActiva, alTeclear } = useDropdownKeyboard({
    cantidad: opciones.length,
    abierto,
    indiceInicial: filtrados.length > 0 ? 1 : 0,
    reiniciarCon: filtro,
    elegir: (i) => {
      if (i === 0 && filtrados.length > 0 && filtro.trim()) {
        elegir(filtrados[0]?.clave ?? '');
      } else {
        elegir(opciones[i] ?? (filtrados[0]?.clave ?? ''));
      }
    },
    cerrar: () => setAbierto(false),
  });

  useEffect(() => {
    if (abierto) {
      searchInputRef.current?.focus();
    }
  }, [abierto]);

  const chip = (
    <span
      className={`relative flex min-h-[24px] w-full items-center rounded-[12px] border py-1 pl-2.5 pr-6 text-xs font-bold ${flexClass(regla, align)} ${color ? 'border-transparent' : 'border-border text-muted'}`}
      style={estiloChip(color)}
    >
      <span className={`min-w-0 ${contenido.className}`} style={contenido.style}>
        {texto || placeholder || ''}
      </span>
      {editable && (
        <button
          type="button"
          title={t('grid.smartchipPickFromList')}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setAbierto((a) => !a);
          }}
          className="absolute right-1 flex h-5 w-5 items-center justify-center rounded-full opacity-70 hover:opacity-100"
        >
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      )}
    </span>
  );

  return (
    <div ref={contenedorRef} className="px-1">
      {chip}
      {editable &&
        abierto &&
        createPortal(
          <div
            ref={panelRef}
            tabIndex={-1}
            role="menu"
            data-dropdown-open="1"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (filtrados.length > 0 && (indice <= 0 || !opciones[indice])) {
                  e.preventDefault();
                  elegir(filtrados[0]!.clave);
                  return;
                }
              }
              alTeclear(e);
            }}
            style={{
              position: 'fixed',
              top: posicion.top,
              left: posicion.left,
              // As wide as the longest option until resized, never past the window's edge.
            width: size?.width ?? 'max-content',
            minWidth: size ? undefined : 140,
            maxWidth: window.innerWidth - posicion.left - MARGEN_BORDE,
            height: size?.height,
            maxHeight: size ? undefined : posicion.altoMaximo,
              transform: posicion.haciaArriba ? 'translateY(calc(-100% - 4px))' : undefined,
              marginTop: posicion.haciaArriba ? 0 : 4,
            }}
            className="z-50 flex flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-card outline-none"
          >
            <div className={`min-h-0 flex-1 overflow-y-auto p-1`}>
            <div className="p-1 pb-1.5">
              <input
                ref={searchInputRef}
                // So the options, not the input's default width, set the list's width.
                size={1}
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder={t('grid.smartchipFilterPlaceholder')}
                className="h-6 w-full rounded border border-border bg-surface-2 px-2 text-xs text-text outline-none focus:border-brand"
              />
            </div>
            <button
              type="button"
              ref={indice === 0 ? refActiva : undefined}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                elegir('');
              }}
              onMouseEnter={() => setIndice(0)}
              className={`block w-full rounded-lg px-2 py-1 text-left text-sm text-muted hover:bg-surface-3 ${indice === 0 ? 'bg-surface-3' : ''}`}
            >
              {defaultPlaceholder}
            </button>
            {filtrados.length === 0 && (
              <p className="px-2 py-1.5 text-sm text-muted">{t('grid.smartchipNoMatches')}</p>
            )}
            {filtrados.map((item, i) => (
              <button
                key={item.clave}
                type="button"
                ref={indice === i + 1 ? refActiva : undefined}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.stopPropagation();
                  elegir(item.clave);
                }}
                onMouseEnter={() => setIndice(i + 1)}
                className={`block w-full rounded-lg px-2 py-0.5 text-left hover:bg-surface-3 ${indice === i + 1 ? 'bg-surface-3' : ''}`}
              >
                <span
                  className="inline-block max-w-full truncate rounded-full border border-transparent px-2.5 py-0.5 text-xs font-bold"
                  style={estiloChip(item.color)}
                >
                  {item.texto}
                </span>
              </button>
            ))}
            </div>
            <EditOptionsFooter
              onEditOptions={onEditOptions && (() => {
                setAbierto(false);
                onEditOptions();
              })}
              onResize={startResize}
              onResetSize={resetSize}
            />
          </div>,
          document.body,
        )}
    </div>
  );
}

export const CeldaChipLista = ListChipCell;
export const EditorChipLista = ListChipEditor;
export default ListChipCell;
